import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, UsersRound, UserCheck, UserCog, TrendingUp, Calendar, User } from "lucide-react";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import { DBS_TYPES } from "../lib/constants";
import { DbsChip } from "./People";

interface Group {
  id: string; name: string; leader_id: string | null; leader_name?: string;
  meeting_rhythm: string | null; active: number; participant_count: number;
  dbs_type?: string | null; created_at: number;
}
interface Leader { id: string; name: string }
interface Participant { id: string; name: string; faith_status: string }

export default function Groups() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<Group | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);

  const load = async () => {
    const [g, l] = await Promise.all([
      api.get<{ groups: Group[] }>("/groups"),
      api.get<{ leaders: Leader[] }>("/leaders"),
    ]);
    setGroups(g.groups); setLeaders(l.leaders);
  };
  useEffect(() => { load(); }, []);

  const openGroup = async (g: Group) => {
    setViewing(g);
    const r = await api.get<{ group: Group; participants: Participant[] }>(`/groups/${g.id}`);
    setParticipants(r.participants);
  };

  const totalParticipants = groups.reduce((s, g) => s + g.participant_count, 0);
  const activeLeaders = new Set(groups.filter((g) => g.leader_id).map((g) => g.leader_id)).size;
  const avgSize = groups.length ? +(totalParticipants / groups.length).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Groups</h1>
          <p className="text-ink-500 text-sm">Manage DBS groups and assign leaders</p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> Create Group</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Mini label="Total Groups" value={groups.length} icon={<UsersRound className="h-5 w-5" />} />
        <Mini label="Total Participants" value={totalParticipants} icon={<UserCheck className="h-5 w-5" />} />
        <Mini label="Active Leaders" value={activeLeaders} icon={<UserCog className="h-5 w-5" />} />
        <Mini label="Avg Group Size" value={avgSize} accent="text-brand" icon={<TrendingUp className="h-5 w-5" />} />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((g) => (
          <Link key={g.id} to={`/groups/${g.id}`} className="card p-5 text-left hover:ring-brand hover:shadow-md transition block">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-lg truncate">{g.name}</div>
              <div className="flex items-center gap-1 shrink-0">
                <DbsChip id={g.dbs_type} />
                {g.active ? <span className="badge bg-green-100 text-green-800">Active</span> : <span className="badge bg-ink-100 text-ink-500">Inactive</span>}
              </div>
            </div>
            <div className="mt-3 text-sm space-y-2 text-ink-700">
              <div className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-ink-400" /><span className="text-ink-500">Leader:</span> <span className="font-medium">{g.leader_name ?? "—"}</span></div>
              <div className="flex items-center gap-2"><UsersRound className="h-3.5 w-3.5 text-ink-400" /><span className="text-ink-500">Participants:</span> <span className="font-medium">{g.participant_count} member{g.participant_count === 1 ? "" : "s"}</span></div>
              <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-ink-400" /><span className="text-ink-500">Rhythm:</span> <span className="font-medium">{g.meeting_rhythm ?? "—"}</span></div>
            </div>
          </Link>
        ))}
        {groups.length === 0 && <div className="text-ink-400">No groups yet — create your first.</div>}
      </div>

      <Modal open={adding} onClose={() => setAdding(false)} title="Create Group">
        <GroupForm leaders={leaders} onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
      </Modal>

      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing?.name ?? ""} wide
        footer={<button className="btn-secondary" onClick={() => setViewing(null)}>Close</button>}>
        {viewing && (
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-ink-500 text-xs uppercase">Leader</div>
              <div>{viewing.leader_name ?? "—"}</div>
            </div>
            <div>
              <div className="text-ink-500 text-xs uppercase">Participants ({participants.length})</div>
              <ul className="list-disc pl-5 mt-1">
                {participants.map((p) => <li key={p.id}>{p.name}</li>)}
                {participants.length === 0 && <li className="list-none text-ink-400">No participants yet</li>}
              </ul>
            </div>
            <div>
              <div className="text-ink-500 text-xs uppercase">Meeting Rhythm</div>
              <div>{viewing.meeting_rhythm ?? "—"}</div>
            </div>
            <div>
              <div className="text-ink-500 text-xs uppercase">Created</div>
              <div>{new Date(viewing.created_at * 1000).toLocaleDateString()}</div>
            </div>
            <div className="pt-2 border-t border-ink-100">
              <GroupForm leaders={leaders} initial={viewing} onSaved={() => { setViewing(null); load(); }} onCancel={() => setViewing(null)}
                onDelete={async () => {
                  if (!confirm("Delete this group?")) return;
                  await api.del(`/groups/${viewing.id}`);
                  setViewing(null); load();
                }} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Mini({ label, value, accent = "text-ink-900", icon }: { label: string; value: number; accent?: string; icon?: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-2xl font-semibold ${accent}`}>{value}</div>
          <div className="text-xs text-ink-500 mt-1">{label}</div>
        </div>
        {icon && <div className="text-ink-300">{icon}</div>}
      </div>
    </div>
  );
}

function GroupForm({
  leaders, initial, onSaved, onCancel, onDelete,
}: {
  leaders: Leader[];
  initial?: Partial<Group>;
  onSaved: () => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    leader_id: initial?.leader_id ?? "",
    meeting_rhythm: initial?.meeting_rhythm ?? "",
    active: initial?.active ?? 1,
    dbs_type: initial?.dbs_type ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = { ...form, leader_id: form.leader_id || null, dbs_type: form.dbs_type || null };
      if (initial && (initial as Group).id) {
        await api.patch(`/groups/${(initial as Group).id}`, payload);
      } else {
        await api.post("/groups", payload);
      }
      onSaved();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-3">
      <div>
        <label className="label">Group name</label>
        <input className="input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </div>
      <div>
        <label className="label">Leader</label>
        <select className="input" value={form.leader_id ?? ""} onChange={(e) => setForm((f) => ({ ...f, leader_id: e.target.value }))}>
          <option value="">— Unassigned —</option>
          {leaders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      <div>
        <label className="label">Meeting rhythm</label>
        <input className="input" placeholder="e.g. Weekly — Thursdays 7:00 PM"
          value={form.meeting_rhythm ?? ""} onChange={(e) => setForm((f) => ({ ...f, meeting_rhythm: e.target.value }))} />
      </div>
      <div>
        <label className="label">DBS type</label>
        <select className="input" value={form.dbs_type ?? ""} onChange={(e) => setForm((f) => ({ ...f, dbs_type: e.target.value }))}>
          <option value="">— Not set —</option>
          {DBS_TYPES.map((d) => <option key={d.id} value={d.id}>{d.label} — {d.blurb}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked ? 1 : 0 }))} />
        Active
      </label>
      {err && <div className="text-sm text-brand">{err}</div>}
      <div className="flex justify-between pt-2">
        <div>{onDelete && <button type="button" onClick={onDelete} className="text-brand text-sm">Delete</button>}</div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </form>
  );
}
